/**
 * Browser-side persistence for the React Query cache.
 *
 * Without this the cache lives only in memory: every reload, every return to the tab from a
 * killed background process, and every hard navigation starts from an empty cache and
 * refetches everything that is already on screen a moment later. Persisting it means the
 * first paint after a reload is the list you were just looking at, and the network call (if
 * the entry has gone stale) happens behind it instead of in front of it.
 *
 * <b>Two storages, on purpose.</b> This app's cache holds two very different kinds of thing:
 *
 *   - Public catalogue data — jobs, companies, taxonomy. Identical for everyone, safe to sit
 *     on disk, and the most useful thing to keep across sessions. Goes to `localStorage`.
 *   - Personal and admin data — the CV bank, one candidate's CV, a profile, saved jobs,
 *     interview lists. This is other people's phone numbers and addresses. Written to
 *     `sessionStorage` instead, so it survives a reload and in-tab navigation (which is what
 *     makes paging through the CV bank feel instant) but is gone when the tab closes, rather
 *     than left on a shared or borrowed machine indefinitely.
 *
 * Both are cleared outright on logout / session expiry — see App.js.
 */

// Bumped whenever the shape of what we cache changes. A mismatch throws the stored cache
// away rather than rehydrating entries the new code cannot read.
const BUSTER = "v1";

const PUBLIC_KEY = "diil_rq_public";
const SESSION_KEY = "diil_rq_session";

// Public data is worth keeping for a day; a job posting does not change minute to minute,
// and anything stale is still revalidated in the background on use.
const PUBLIC_MAX_AGE = 24 * 60 * 60 * 1000;

// Personal data only has to outlive a reload. sessionStorage already ends it at tab close;
// this caps it for a tab left open overnight.
const SESSION_MAX_AGE = 60 * 60 * 1000;

// First segment of the query key. Anything listed here is personal or admin-only and never
// reaches localStorage. Unknown keys are treated as private too — see isPrivateKey: a new
// query added elsewhere in the app should fail closed, not quietly start persisting
// somebody's data to disk.
const PUBLIC_KEYS = new Set([
    "jobs", "job", "jobsByRole", "jobsByLocation", "companies", "company",
    "taxonomy", "categories", "locations", "search", "tests",
]);

const isPrivateKey = (queryKey) => !PUBLIC_KEYS.has(Array.isArray(queryKey) ? queryKey[0] : queryKey);

// Storage can be absent or throw outright: Safari private mode, an iOS webview with site
// data disabled, or a full quota. None of that is a reason to break the app — the cache is
// an optimisation, so every access degrades to "no persistence".
const safeGet = (storage, key) => {
    try {
        return storage.getItem(key);
    } catch {
        return null;
    }
};

const safeSet = (storage, key, value) => {
    try {
        storage.setItem(key, value);
    } catch {
        // Almost always the quota. Drop what is there rather than leaving a half-written or
        // stale blob behind, and carry on with an in-memory cache.
        try {
            storage.removeItem(key);
        } catch {
            /* nothing left to do */
        }
    }
};

const safeRemove = (storage, key) => {
    try {
        storage.removeItem(key);
    } catch {
        /* nothing left to do */
    }
};

const readBucket = (storage, key, maxAge) => {
    const raw = safeGet(storage, key);
    if (!raw) return null;

    try {
        const parsed = JSON.parse(raw);
        if (parsed?.buster !== BUSTER || Date.now() - (parsed.timestamp || 0) > maxAge) {
            safeRemove(storage, key);
            return null;
        }
        return parsed;
    } catch {
        // Corrupt or written by an older shape.
        safeRemove(storage, key);
        return null;
    }
};

/**
 * A persister in the shape persistQueryClient expects, writing one dehydrated cache across
 * the two storages above and reading it back as one.
 */
export const splitPersister = {
    persistClient: (persisted) => {
        const queries = persisted?.clientState?.queries || [];
        const stamp = { buster: BUSTER, timestamp: Date.now() };

        safeSet(window.localStorage, PUBLIC_KEY, JSON.stringify({
            ...stamp,
            clientState: {
                mutations: [],
                queries: queries.filter((q) => !isPrivateKey(q.queryKey)),
            },
        }));

        safeSet(window.sessionStorage, SESSION_KEY, JSON.stringify({
            ...stamp,
            clientState: {
                mutations: [],
                queries: queries.filter((q) => isPrivateKey(q.queryKey)),
            },
        }));
    },

    restoreClient: () => {
        const pub = readBucket(window.localStorage, PUBLIC_KEY, PUBLIC_MAX_AGE);
        const priv = readBucket(window.sessionStorage, SESSION_KEY, SESSION_MAX_AGE);
        if (!pub && !priv) return undefined;

        // Each bucket was already age-checked against its own limit, so the timestamp handed
        // back is `now` — the library's own maxAge must not expire one bucket on the other's
        // clock.
        return {
            buster: BUSTER,
            timestamp: Date.now(),
            clientState: {
                mutations: [],
                queries: [
                    ...(pub?.clientState?.queries || []),
                    ...(priv?.clientState?.queries || []),
                ],
            },
        };
    },

    removeClient: () => {
        safeRemove(window.localStorage, PUBLIC_KEY);
        safeRemove(window.sessionStorage, SESSION_KEY);
    },
};

export const persistOptions = {
    persister: splitPersister,
    buster: BUSTER,
    // The restore side is handled per bucket above; this only has to be permissive enough
    // not to veto either of them.
    maxAge: PUBLIC_MAX_AGE,
    dehydrateOptions: {
        // Only settled, successful queries. A persisted error would be restored as a failed
        // screen the user never saw fail, and a pending one restores as a request that will
        // never resolve.
        // Also skip oversized job pages: the map asks for 500 ads at a time, and writing
        // that to storage means a multi-MB JSON.stringify on the main thread every persist
        // tick — for data that blows the ~5 MB mobile quota and gets dropped anyway.
        shouldDehydrateQuery: (query) => (
            query.state.status === "success" &&
            query.state.data !== undefined &&
            !(Array.isArray(query.state.data?.jobs) && query.state.data.jobs.length > 200)
        ),
        // Mutations are actions, not data. Replaying one on reload would repeat a write.
        shouldDehydrateMutation: () => false,
    },
};

export default persistOptions;
